// require('remirror/styles/all.css')

import { PropTypes } from 'prop-types'
import React, { forwardRef, useCallback, useEffect } from 'react'
import { componentsTheme } from '@remirror/theme'

import {
  EditorComponent,
  OnChangeHTML,
  OnChangeJSON,
  Remirror,
  useDocChanged,
  useRemirror,
} from '@remirror/react'
import { useEditorFocus } from '@remirror/react-hooks'
import {
  BlockquoteExtension,
  BoldExtension,
  BulletListExtension,
  DropCursorExtension,
  HardBreakExtension,
  HeadingExtension,
  HorizontalRuleExtension,
  IframeExtension,
  ImageExtension,
  ItalicExtension,
  LinkExtension,
  ListExtension,
  OrderedListExtension,
  PlaceholderExtension,
} from 'remirror/extensions'

import EditorMenu from './EditorMenu'
import Uploader from './Uploader'

function uploadHandler(progressContainer, fileInput, previewPath) {
  return (files) => (
    files.map(({ file }) => {
      const uploader = new Uploader(file, progressContainer, fileInput)

      return () => new Promise((resolve) => (
        uploader.upload(({ filename: fileName, signed_id: signedID }) => {
          resolve({
            fileName,
            signedID,
            src:
              previewPath
                .replace(':signed_id:', signedID)
                .replace(':filename:', fileName),
          })
        })
      ))
    })
  )
}

const OnChangeText = ({ onChange }) => {
  useDocChanged(
    useCallback(
      ({ state }) => {
        const text = state.doc.textBetween(
          0,
          state.doc.content.size,
          "\n\n",
          leaf => {
            switch (leaf.type.name) {
              case 'iframe':
                const { src } = leaf.attrs
                return `[${src}]`
              case 'image':
                const { title, fileName } = leaf.attrs
                return `[${title.length > 0 ? title : fileName}]`
              default:
                return ''
            }
          }
        )

        onChange(text)
      },
      [onChange]
    )
  )

  return null
}

const Editor = forwardRef(function Editor({
  field,
  value,
  onChangeData,
  onChangeHTML,
  onChangeText,
  progressContainer,
  fileInput,
  previewPath,
}, ref) {
  const { getContext, manager, state, setState, onChange } = useRemirror({
    content: value,
    extensions: () => [
      new BlockquoteExtension(),
      new BoldExtension(),
      new BulletListExtension(),
      new DropCursorExtension(),
      new HardBreakExtension(),
      new HeadingExtension(),
      new HorizontalRuleExtension(),
      new IframeExtension({ class: 'w-full' }),
      new ImageExtension({
        enableResizing: true,
        extraAttributes: {
          signedID: {
            default: null,
            parseDOM: (dom) => {
              return dom.dataSet('signedId')
            },
            toDOM: (attrs) => {
              return ['data-signed-id', attrs.signedID]
            },
          },
        },
        uploadHandler:
          uploadHandler(progressContainer, fileInput, previewPath),
      }),
      new ItalicExtension(),
      new LinkExtension({ autoLink: true, selectTextOnClick: true }),
      new OrderedListExtension(),
      new PlaceholderExtension({ placeholder: 'Festina lente…' }),
    ],
  })

  useEffect(() => {
    // Force an update to ensure the first change is recorded. This is a work
    // around for https://github.com/remirror/remirror/issues/2298.
    getContext().commands.emptyUpdate()

    field.addEventListener('focus', event => {
      getContext().focus('start')
    })
  }, [field, getContext])

  return (
    <Remirror
      classNames={['prose', 'prose-stone']}
      initialContent={state}
      manager={manager}
      onChange={onChange}
    >
      <div className="relative">
        <div className="absolute bg-stone-200 m-px h-[2.25rem] left-0 right-0 z-10">
          <EditorMenu
            fileInput={fileInput}
            previewPath={previewPath}
            progressContainer={progressContainer}
          />
        </div>
        <EditorComponent />
      </div>
      <OnChangeHTML onChange={onChangeHTML} />
      <OnChangeJSON onChange={onChangeData} />
      <OnChangeText onChange={onChangeText} />
    </Remirror>
  )
}, [])

Editor.propTypes = {
  onChangeData: PropTypes.func.isRequired,
  onChangeHTML: PropTypes.func.isRequired,
  onChangeText: PropTypes.func.isRequired,
}

export default Editor
